import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Stack } from '@mui/material';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_LISTING_REQUESTS } from 'new/constants/pathnames';
import { BannerDescription } from 'new/elements/BannerDescription';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { TabProps, Tabs } from 'new/elements/Tabs/Tabs';
import { loadIncomingListingRequests, loadOutgoingListingRequests } from 'js/actions';
import styles from './ListingRequests.module.scss';
import { RequestsTable } from '../components/RequestsTable/RequestsTable';
import {
  GET_INCOMING_LISTING_REQUESTS_REQUEST,
  GET_OUTGOING_LISTING_REQUESTS_REQUEST,
} from 'js/constants/actionTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useMarketplaceRequests } from 'new/hooks/adapters/useMarketplaceRequests';

interface ListingRequestsProps {}

const description = 'Respond to requests to add Nexsets as Data Products in the Marketplace.';

const tabs = [
  {
    value: 'incoming',
    label: 'Incoming',
  },
  {
    value: 'outgoing',
    label: 'Outgoing',
  },
] as TabProps[];

export const ListingRequests: ScreenComponent<ListingRequestsProps> = (
  props: ListingRequestsProps,
) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const user = useCurrentUser();
  const isCustodian = user.isOrgCustodian || user.isDomainCustodian;
  const isLoading = useIsLoading(
    GET_OUTGOING_LISTING_REQUESTS_REQUEST,
    GET_INCOMING_LISTING_REQUESTS_REQUEST,
  );
  const { outgoingListingRequests: outgoingItems, incomingListingRequests } =
    useMarketplaceRequests();
  const incomingItems = isCustodian ? incomingListingRequests : [];
  const visibleTabs = isCustodian ? tabs : tabs.filter((tab) => tab.value === 'outgoing');
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0]);

  useEffect(() => {
    dispatch(loadOutgoingListingRequests());
    if (isCustodian) {
      dispatch(loadIncomingListingRequests());
    }
  }, []);

  const onTabChange = (e, value) => {
    setSelectedTab(tabs.find((tab) => tab.value === value));
  };

  return (
    <>
      <BannerDescription title="Listing Requests" description={description} />
      <Stack className={styles.tabsContainer}>
        <Tabs value={selectedTab?.value} tabs={visibleTabs} onChange={onTabChange} />
      </Stack>
      <RequestsTable
        selectedTab={selectedTab?.value}
        requestType="listing"
        loading={isLoading}
        incomingItems={incomingItems}
        outgoingItems={outgoingItems}
      />
    </>
  );
};

ListingRequests.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_LISTING_REQUESTS}`,
  domain: 'marketplace',
  header: {
    title: 'Listing Requests',
    slugTitle: 'Listing Requests',
  },
  screenName: 'Listing Requests',
} as ScreenConfig<ListingRequestsProps>;
